import { isCalendarDate, roundToCents, z } from '@dcp/shared'
import { formatCurrency, formatDate } from '@dcp/shared/utils/formatters'
import type { CreditCardAvailabilityRow } from '@/lib/db-tenant'

/**
 * The "Edit credit card" form, and the patch of manual values a save sends to
 * `save_credit_card_manual_values`.
 *
 * TWO UNITS. The table and the RPC carry amounts signed as stored — what is
 * owed is NEGATIVE (ADR 0017).
 * The form shows what is owed as a positive amount, so Current Balance and
 * Statement Balance are negated on the way in and out; Total Credit Limit is
 * positive in both.
 */

/** The four figures a manual value can replace. */
export type CreditCardValueField = 'credit_limit' | 'balance' | 'payment_due_date' | 'statement_balance'

type AmountField = Exclude<CreditCardValueField, 'payment_due_date'>

const AMOUNT_ERROR = 'Enter a number, e.g. 25000.00'

/** The largest amount the manual value columns (`NUMERIC(15,2)`) hold. */
const AMOUNT_MAX = 9_999_999_999_999.99

/** `save_credit_card_manual_values` refuses a longer note (22023). */
export const NOTE_MAX_LENGTH = 500

/** What the server's `btrim(p_note, E' \t\r\n')` strips — and nothing else. */
const NOTE_TRIMMED = new Set([' ', '\t', '\r', '\n'])

/**
 * The note as the server stores it: stripped of leading and trailing spaces,
 * tabs, CR and LF only. Not `String.prototype.trim`, which also strips NBSP,
 * `\v`, `\f` and other Unicode spaces the server keeps — a note padded with
 * them would pass the field's length check and then be refused on save.
 */
export function trimNote(note: string): string {
  let start = 0
  let end = note.length
  while (start < end && NOTE_TRIMMED.has(note[start])) start++
  while (end > start && NOTE_TRIMMED.has(note[end - 1])) end--
  return note.slice(start, end)
}

/**
 * `AmountInput` reports unparseable text as NaN, which `z.number` rejects with
 * this message.
 *
 * An empty amount is `null` in the form, never `undefined`: react-hook-form
 * reads a field set to `undefined` as its default value, so a saved figure that
 * is cleared or reverted to "not provided" would keep showing the saved number.
 */
const amount = z
  .number({ invalid_type_error: AMOUNT_ERROR })
  // The columns are NUMERIC(15,2): a larger figure fails the save with 22003.
  .max(AMOUNT_MAX, 'Enter an amount up to 9,999,999,999,999.99')

const OWED_ERROR = 'Enter the amount owed (0 or more)'

/**
 * The Edit form schema for `row`. The server's own rules (negative limit,
 * credit balance, long note) are repeated here so they show on the field
 * instead of as a failed save.
 *
 * Current Balance (Usage) and Statement Balance take only 0 or an amount owed —
 * in form units, nothing negative. On a linked card the integration's own
 * figure passes even when it is a credit: the dialog shows it on an untouched
 * field, which a save does not send, and the server stores a typed figure equal
 * to the integration's as no manual value.
 */
export function creditCardEditSchema(row: CreditCardAvailabilityRow) {
  const owed = (field: 'balance' | 'statement_balance') =>
    amount
      .nullable()
      .refine(
        (value) =>
          value === null ||
          value >= 0 ||
          (row.source === 'linked' && sameDraftValue(value, integrationDraft(row)[field])),
        OWED_ERROR,
      )

  return z.object({
    credit_limit: amount.nonnegative('The credit limit cannot be negative').nullable(),
    balance: owed('balance'),
    statement_balance: owed('statement_balance'),
    /**
     * ISO `YYYY-MM-DD`, `''` when cleared, or {@link INVALID_DATE_ENTRY} while
     * the typed text is not a date.
     */
    payment_due_date: z
      .string()
      .refine((value) => value === '' || isCalendarDate(value), 'Enter the date as mm/dd/yyyy'),
    // Measured trimmed and in code points, as the server's `char_length(btrim(...))`:
    // `length` counts UTF-16 units, so an emoji would count twice.
    note: z
      .string()
      .refine(
        (value) => [...trimNote(value)].length <= NOTE_MAX_LENGTH,
        `Keep the note to ${NOTE_MAX_LENGTH} characters or fewer`,
      ),
  })
}

export type CreditCardEditValues = z.infer<ReturnType<typeof creditCardEditSchema>>

/**
 * What the form holds for Payment Due Date while the input holds text that is
 * not a date (`DateField` reports such an entry as `'incomplete'`). Never a
 * calendar date and never `''`, so the schema always refuses it and it can
 * neither be saved nor read as a cleared date.
 */
export const INVALID_DATE_ENTRY = 'invalid date entry'

/**
 * The four value fields of the form, in form units. Spelled out rather than
 * picked from {@link CreditCardEditValues}: the schema is built from
 * {@link integrationDraft}, so a derived type would be circular.
 */
export interface CreditCardValueDraft {
  credit_limit: number | null
  balance: number | null
  statement_balance: number | null
  payment_due_date: string
}

/**
 * What one save changes — `p_changes` of `save_credit_card_manual_values`.
 * Amounts are signed as stored. An absent key keeps the stored value; `null`
 * removes the manual value, so the figure follows the integration (a linked
 * card) or is empty (a manual card), and clears the note.
 *
 * A type alias, not an interface: only an alias is assignable to the RPC's
 * `Json` argument.
 */
export type CreditCardPatch = {
  credit_limit?: number | null
  balance?: number | null
  statement_balance?: number | null
  payment_due_date?: string | null
  note?: string | null
}

/**
 * The value fields the user put back to the integration with Revert since
 * last editing them. Kept beside the form, not derived from the figures: a
 * typed number cannot tell "follow the integration" from "pin this figure".
 */
export type FollowedFields = Partial<Record<CreditCardValueField, boolean>>

/** Which fields react-hook-form reports as changed from what the dialog was seeded with. */
export type ChangedFields = Partial<Record<keyof CreditCardEditValues, boolean>>

/** Shown where the integration has no value for a field. */
export const NOT_PROVIDED = 'not provided'

/** Fields whose stored sign is negative for an amount owed. */
const OWED: Record<AmountField, boolean> = {
  credit_limit: false,
  balance: true,
  statement_balance: true,
}

const AMOUNT_FIELDS: readonly AmountField[] = ['credit_limit', 'balance', 'statement_balance']

function toFormAmount(field: AmountField, stored: number | null): number | null {
  if (stored === null) return null
  // `0 - x`, not `-x`: negating a zero would give -0, which renders as "-0.00".
  return OWED[field] ? 0 - stored : stored
}

function toStoredAmount(field: AmountField, form: number | null): number | null {
  if (form === null) return null
  return OWED[field] ? 0 - form : form
}

/** Form values seeded from what the table shows (the effective figures). */
export function creditCardEditDefaults(row: CreditCardAvailabilityRow): CreditCardEditValues {
  return {
    credit_limit: toFormAmount('credit_limit', row.credit_limit),
    balance: toFormAmount('balance', row.balance),
    statement_balance: toFormAmount('statement_balance', row.statement_balance),
    payment_due_date: row.payment_due_date ?? '',
    note: row.note ?? '',
  }
}

/** The integration's figures in form units — what Revert puts back. */
export function integrationDraft(row: CreditCardAvailabilityRow): CreditCardValueDraft {
  return {
    credit_limit: toFormAmount('credit_limit', row.credit_limit_integration),
    balance: toFormAmount('balance', row.balance_integration),
    statement_balance: toFormAmount('statement_balance', row.statement_balance_integration),
    payment_due_date: row.payment_due_date_integration ?? '',
  }
}

/**
 * Whether two values of one form field are the same figure. Amounts compare to
 * the cent; NaN (unparseable text) never equals anything.
 */
export function sameDraftValue(a: number | string | null, b: number | string | null): boolean {
  if (typeof a === 'number' && typeof b === 'number') return roundToCents(a) === roundToCents(b)
  return a === b
}

/**
 * The patch a save of `values` sends: only what the user changed, so a save
 * never rewrites a figure from what the dialog was seeded with — that row can
 * be stale, and someone else may have changed the figure since.
 *
 * A field in `followed` (Revert) sends `null`, whatever the dialog shows as the
 * integration figure. Otherwise a field in `changed` (react-hook-form's
 * `dirtyFields`, which drops a field typed back to its seeded value) sends its
 * value in the stored sign, a cleared field `null`; the note is trimmed as the
 * server trims it ({@link trimNote}). An empty patch means nothing to save.
 */
export function creditCardPatchFromForm(
  values: CreditCardEditValues,
  changed: ChangedFields,
  followed: FollowedFields,
): CreditCardPatch {
  const patch: CreditCardPatch = {}
  for (const field of AMOUNT_FIELDS) {
    if (followed[field]) patch[field] = null
    else if (changed[field]) patch[field] = toStoredAmount(field, values[field])
  }
  if (followed.payment_due_date) patch.payment_due_date = null
  else if (changed.payment_due_date) patch.payment_due_date = values.payment_due_date || null
  if (changed.note) patch.note = trimNote(values.note) || null
  return patch
}

/**
 * The integration's value of `field`, formatted as the table column formats it
 * (signed as stored, mm/dd/yyyy), or {@link NOT_PROVIDED}.
 */
export function formatIntegrationValue(
  row: CreditCardAvailabilityRow,
  field: CreditCardValueField,
): string {
  if (field === 'payment_due_date') {
    const date = row.payment_due_date_integration
    return date ? formatDate(date) : NOT_PROVIDED
  }
  const value = row[`${field}_integration` as const]
  return value === null ? NOT_PROVIDED : formatCurrency(value)
}

/**
 * The integration's value of `field` for the Edit dialog's "From integration"
 * hint: in FORM units, so what is owed reads positive like the input beside it
 * (a credit reads negative), or {@link NOT_PROVIDED}.
 */
export function formatIntegrationHint(
  row: CreditCardAvailabilityRow,
  field: CreditCardValueField,
): string {
  if (field === 'payment_due_date') return formatIntegrationValue(row, field)
  const value = integrationDraft(row)[field]
  return value === null ? NOT_PROVIDED : formatCurrency(value)
}

/** The tooltip on a table figure that a manual value replaced. */
export function manualEditTooltip(row: CreditCardAvailabilityRow, field: CreditCardValueField): string {
  return `Edited manually. From integration: ${formatIntegrationValue(row, field)}`
}
